import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, RefreshCw, BookOpen, Loader2 } from 'lucide-react';
import { Language } from '../types';
import { VocabularyItem, callExtractPageVocabulary } from '../services/geminiService';

interface PageVocabularyPanelProps {
    isOpen: boolean;
    onClose: () => void;
    getPageText: () => Promise<string>;
    pageNumber: number;
    documentTitle?: string;
    boardTitle?: string;
    documentId: string;
    language: Language;
}

type CacheKey = string;
const makeKey = (docId: string, page: number, lang: Language): CacheKey => `${docId}::${page}::${lang}`;

const MIN_WIDTH = 240;
const MAX_WIDTH = 640;
const DEFAULT_WIDTH = 320;

export const PageVocabularyPanel: React.FC<PageVocabularyPanelProps> = ({
    isOpen,
    onClose,
    getPageText,
    pageNumber,
    documentTitle,
    boardTitle,
    documentId,
    language,
}) => {
    const [items, setItems] = useState<VocabularyItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEmpty, setIsEmpty] = useState(false);
    const [width, setWidth] = useState(DEFAULT_WIDTH);
    const [analyzedPage, setAnalyzedPage] = useState<number | null>(null);

    const cacheRef = useRef<Map<CacheKey, VocabularyItem[]>>(new Map());
    const lastLoadedKeyRef = useRef<CacheKey | null>(null);
    const isDraggingRef = useRef(false);
    const dragStartXRef = useRef(0);
    const dragStartWidthRef = useRef(DEFAULT_WIDTH);

    const isVi = language === 'vietnamese';

    const load = useCallback(async (forceRefresh = false) => {
        const key = makeKey(documentId, pageNumber, language);

        if (!forceRefresh && cacheRef.current.has(key)) {
            const cached = cacheRef.current.get(key)!;
            setItems(cached);
            setIsEmpty(cached.length === 0);
            setError(null);
            setAnalyzedPage(pageNumber);
            lastLoadedKeyRef.current = key;
            return;
        }

        setIsLoading(true);
        setError(null);
        setIsEmpty(false);
        setItems([]);

        try {
            const pageText = await getPageText();
            if (!pageText || pageText.trim().length < 30) {
                setIsEmpty(true);
                setIsLoading(false);
                return;
            }
            const result = await callExtractPageVocabulary(pageText, pageNumber, language, documentTitle, boardTitle);
            cacheRef.current.set(key, result);
            lastLoadedKeyRef.current = key;
            setAnalyzedPage(pageNumber);
            setItems(result);
            setIsEmpty(result.length === 0);
        } catch (e: any) {
            const code = e?.code || e?.message || '';
            if (code.includes('resource-exhausted') || code.includes('Hết lượt')) {
                setError(isVi
                    ? 'Bạn đã dùng hết lượt AI hôm nay. Hãy thử lại sau hoặc nâng cấp khi StudyBoard mở gói trả phí.'
                    : "You've reached today's AI limit. Please try again later or upgrade when paid plans are available.");
            } else {
                setError(isVi
                    ? 'Không thể phân tích trang này. Hãy thử lại hoặc chọn trang khác.'
                    : 'Could not analyze this page. Please try again or use another page.');
            }
        } finally {
            setIsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId, pageNumber, language, documentTitle, boardTitle]);

    // Only fire when panel opens — never on page scroll
    useEffect(() => {
        if (isOpen) load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Drag-to-resize handlers
    const handleDragStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isDraggingRef.current = true;
        dragStartXRef.current = e.clientX;
        dragStartWidthRef.current = width;

        const onMove = (ev: MouseEvent) => {
            if (!isDraggingRef.current) return;
            // dragging left = increase width, right = decrease
            const delta = dragStartXRef.current - ev.clientX;
            const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, dragStartWidthRef.current + delta));
            setWidth(newWidth);
        };

        const onUp = () => {
            isDraggingRef.current = false;
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [width]);

    if (!isOpen) return null;

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    // Mobile: bottom sheet sliding up from bottom
    if (isMobile) {
        return (
            <>
                {/* Backdrop */}
                <div
                    className="fixed inset-0 bg-black/30 z-[199] backdrop-blur-sm"
                    onClick={onClose}
                />
                {/* Sheet */}
                <div className="fixed bottom-0 left-0 right-0 z-[200] bg-white dark:bg-slate-900 rounded-t-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom-4 duration-300"
                    style={{ maxHeight: '68vh' }}>
                    {/* Drag pill */}
                    <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
                        <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-slate-600" />
                    </div>
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <BookOpen size={16} className="text-indigo-500" />
                            <span className="font-semibold text-base text-gray-800 dark:text-gray-100">
                                {isVi ? 'Từ khó' : 'Vocabulary'}
                            </span>
                            {analyzedPage != null && (
                                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300">
                                    {isVi ? `Trang ${analyzedPage}` : `Page ${analyzedPage}`}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => load(true)} disabled={isLoading}
                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-40"
                                title={isVi ? 'Làm mới trang hiện tại' : 'Refresh current page'}>
                                <RefreshCw size={15} className={isLoading ? 'animate-spin' : ''} />
                            </button>
                            <button onClick={onClose}
                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                                <X size={18} />
                            </button>
                        </div>
                    </div>
                    {/* Body */}
                    <div className="flex-1 overflow-y-auto">
                        {isLoading && (
                            <div className="flex flex-col items-center justify-center h-32 gap-3 text-gray-400">
                                <Loader2 size={22} className="animate-spin text-indigo-400" />
                                <span className="text-sm">{isVi ? 'Đang phân tích từ vựng trong trang...' : 'Analyzing vocabulary on this page...'}</span>
                            </div>
                        )}
                        {!isLoading && error && (
                            <div className="m-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm leading-relaxed">{error}</div>
                        )}
                        {!isLoading && !error && isEmpty && (
                            <div className="flex flex-col items-center justify-center h-32 gap-2 text-gray-400 px-4 text-center">
                                <BookOpen size={22} className="opacity-40" />
                                <span className="text-sm">{isVi ? 'Trang này không có nhiều từ khó đáng chú ý.' : 'No significant difficult vocabulary found on this page.'}</span>
                            </div>
                        )}
                        {!isLoading && !error && items.length > 0 && (
                            <ul className="divide-y divide-gray-100 dark:divide-slate-800 pb-6">
                                {items.map((item, i) => (
                                    <li key={i} className="px-4 py-4">
                                        <div className="flex items-baseline justify-between gap-2 mb-1.5">
                                            <span className="font-semibold text-base text-gray-900 dark:text-gray-100">{item.word_or_phrase}</span>
                                            <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium flex-shrink-0">{item.vietnamese_meaning}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2">{item.meaning_in_context}</p>
                                        <p className="text-xs text-gray-400 dark:text-gray-500 italic truncate">"{item.source_snippet}"</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </>
        );
    }

    // Desktop: side panel with drag-to-resize
    return (
        <div
            className="fixed top-0 right-0 h-full bg-white dark:bg-slate-900 border-l border-gray-200 dark:border-slate-700 shadow-2xl z-[200] flex flex-col"
            style={{ width }}
        >
            {/* Drag handle */}
            <div
                onMouseDown={handleDragStart}
                className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-indigo-400/40 transition-colors group z-10"
                title={isVi ? 'Kéo để thay đổi kích thước' : 'Drag to resize'}
            >
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-full bg-gray-300 dark:bg-slate-600 group-hover:bg-indigo-400 transition-colors" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0">
                    <BookOpen size={16} className="text-indigo-500 flex-shrink-0" />
                    <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
                        {isVi ? 'Từ khó' : 'Vocabulary'}
                    </span>
                    {analyzedPage != null && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex-shrink-0">
                            {isVi ? `Trang ${analyzedPage}` : `Page ${analyzedPage}`}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                        onClick={() => load(true)}
                        disabled={isLoading}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors disabled:opacity-40"
                        title={isVi ? 'Làm mới trang hiện tại' : 'Refresh current page'}
                    >
                        <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
                {isLoading && (
                    <div className="flex flex-col items-center justify-center h-40 gap-3 text-gray-400">
                        <Loader2 size={20} className="animate-spin text-indigo-400" />
                        <span className="text-sm">
                            {isVi ? 'Đang phân tích từ vựng trong trang...' : 'Analyzing vocabulary on this page...'}
                        </span>
                    </div>
                )}

                {!isLoading && error && (
                    <div className="m-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm leading-relaxed">
                        {error}
                    </div>
                )}

                {!isLoading && !error && isEmpty && (
                    <div className="flex flex-col items-center justify-center h-40 gap-2 text-gray-400 px-4 text-center">
                        <BookOpen size={20} className="opacity-40" />
                        <span className="text-sm">
                            {isVi
                                ? 'Trang này không có nhiều từ khó đáng chú ý.'
                                : 'No significant difficult vocabulary found on this page.'}
                        </span>
                    </div>
                )}

                {!isLoading && !error && items.length > 0 && (
                    <ul className="divide-y divide-gray-100 dark:divide-slate-800">
                        {items.map((item, i) => (
                            <li key={i} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors">
                                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                                    <span className="font-semibold text-base text-gray-900 dark:text-gray-100">
                                        {item.word_or_phrase}
                                    </span>
                                    <span className="text-sm text-indigo-600 dark:text-indigo-400 font-medium flex-shrink-0">
                                        {item.vietnamese_meaning}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-2">
                                    {item.meaning_in_context}
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500 italic truncate">
                                    "{item.source_snippet}"
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
